import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionDropDownField, QuestionButtonsField, QuestionUsTinField } from 'components/Questionnaires/fields';
import { requiredValidator, lengthValidator, sameCharacterValidator, sequentialIntegerValidator, validateQuestion } from 'components/Questionnaires/validators';
import CollapsibleHeader from 'components/Questionnaires/CollapsibleHeader';

export const QuestionUsTinBlock = ({ block }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? { appliedFor: 'No' });
  const [collapsibleAreaShown, setCollapsibleAreaShown] = useState(false);

  const tinValidators = useMemo(() => {
    const conditions = [
      { condition: lengthValidator, message: 'questionnaire.usTin.tin.lengthErrorMessage' },
      { condition: sameCharacterValidator, message: 'questionnaire.usTin.tin.sameCharErrorMessage' },
      { condition: sequentialIntegerValidator, message: 'questionnaire.usTin.tin.sequentialErrorMessage' },
    ];
    if (block.componentProps?.tinRequired !== false) {
      conditions.push({ condition: requiredValidator, message: 'questionnaire.usTin.tin.errorMessage' });
    }

    return { conditions };
  }, [block.componentProps?.tinRequired]);

  const tinType = {
    id: 'tinType',
    label: 'questionnaire.usTin.tinType.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.usTin.tinType.items',
    },
    className: 'col-12',
    default: block.componentProps?.default ?? 'Ssn',
    visible: true,
    answer: answer?.tinType ?? block.componentProps?.default
  };
  const [usTin, setUsTin] = useState({
    id: 'tin',
    component: QuestionUsTinField,
    componentProps: {
      tinFormat: answer?.tinType?.toUpperCase() === 'EIN' ? 'EIN' : 'SSN',
      length: 9
    },
    label: 'questionnaire.usTin.tin.label',
    info: {
      instructions: 'questionnaire.usTin.tin.info',
    },
    className: 'col-12',
    visible: true,
    validation: tinValidators,
    answer: answer?.usTin
  });
  const appliedFor = {
    id: 'appliedFor',
    label: 'questionnaire.usTin.appliedFor.label',
    component: QuestionButtonsField,
    componentProps: {
      buttons: [
        { value: 'Yes', text: 'questionnaire.buttons.Yes' },
        { value: 'No', text: 'questionnaire.buttons.No' }
      ]
    },
    visible: block.componentProps?.showAppliedFor !== undefined ? block.componentProps?.showAppliedFor : true,
    answer: answer?.appliedFor
  };

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    if (block.componentProps?.tinRequired === false) {
      return !answer.tin || usTin.isValid;
    }
    if (block.componentProps?.showAppliedFor === false) {
      return usTin.isValid;
    }
    if (answer.tin === undefined) {
      return undefined;
    }
    return usTin.isValid || (!answer.tin && (answer.appliedFor === undefined || answer.appliedFor === 'Yes'));
  };

  useEffect(() => {
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'tinType': {
        const newComponentProps = { ...usTin.componentProps, ...{ tinFormat: value?.toUpperCase() === 'EIN' ? 'EIN' : 'SSN' } };
        setUsTin({ ...usTin, componentProps: newComponentProps });
        setAnswer({ ...answer, tinType: value });
        break;
      }
      case 'tin': {
        const newUsTin = { ...q, answer: value };
        validateQuestion(newUsTin);
        setUsTin(newUsTin);
        setAnswer({ ...answer, tin: value });
        break;
      }
      case 'appliedFor': {
        const newComponentProps = { ...usTin.componentProps, ...{ disabled: value === 'Yes' } };
        const validators = value === 'Yes' ? null : tinValidators;
        const questionProps = value === 'Yes' ? { isValid: true, errorMessages: undefined } : null;
        const newUsTin = { ...usTin, ...questionProps, ...{ validation: validators, componentProps: newComponentProps } };
        validateQuestion(newUsTin);
        setUsTin(newUsTin);
        setAnswer({ ...answer, appliedFor: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      onQuestionChange({ ...usTin, isForceEdited: true }, answer?.tin ?? '');
    }
  }, [block.isForceEdited]);

  return (<div className="container row">
    <Question key={ tinType.id } blockId={ blockIdentifier } question={ tinType } { ...tinType.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ usTin.id } blockId={ blockIdentifier } question={ usTin } { ...usTin.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { appliedFor.visible && <div className="col-12">
      <CollapsibleHeader isShown={ collapsibleAreaShown } onExpandCollapse={ value => setCollapsibleAreaShown(value) } text="questionnaire.usTin.moreOptions" />
      { collapsibleAreaShown && <Question key={ appliedFor.id } blockId={ blockIdentifier } question={ appliedFor } { ...appliedFor.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    </div> }
  </div>);
};

QuestionUsTinBlock.displayName = 'QuestionUsTinBlock';

QuestionUsTinBlock.propTypes = {
  block: PropTypes.shape().isRequired
};

QuestionUsTinBlock.defaultProps = {
};

export default QuestionUsTinBlock;
